import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for recovery tokens in URL hash BEFORE Supabase processes them
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const tokenType = hashParams.get("type");
    const accessToken = hashParams.get("access_token");
    
    // Also check query params for recovery type
    const urlParams = new URLSearchParams(window.location.search);
    const queryType = urlParams.get("type");
    
    // Redirect to /auth/callback if recovery token detected on any other page
    if (window.location.pathname !== "/auth/callback") {
      if ((accessToken && tokenType === "recovery") || queryType === "recovery") {
        console.log("Recovery token detected on non-callback page, redirecting to /auth/callback");
        window.location.href = `/auth/callback${window.location.search}${window.location.hash}`;
        return;
      }
    }

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event, session?.user?.email);
        
        // Detect PASSWORD_RECOVERY event and redirect to show password form
        if (event === "PASSWORD_RECOVERY") {
          console.log("PASSWORD_RECOVERY event detected, redirecting to password form");
          setTimeout(() => {
            window.location.href = "/auth/callback?show_password_form=true";
          }, 0);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, isAuthenticated: !!session }}>
      {children}
    </AuthContext.Provider>
  );
};
